import { useQuery, useQueryClient } from "@tanstack/react-query";
import hashObject from "object-hash";
import { useState, useEffect } from 'react';
import moment, { Moment } from "moment-timezone";
import ExcelJS from "exceljs";
import { z } from "zod";
import Decimal from "decimal.js";
import * as Excel from "../excel";
import { safeParseDecimal } from "../excel";
import { S3ObjectItem } from "../models/trading";
import { BaseDbObject } from "../models/db";
import { DbUser } from "../models/index";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPatch,
  fetchApiPost,
  fetchApiPut,
} from "./utils";
import {
  PossibleReturnType,
} from "../responseResults";
import { CommonDestinationTimezone } from "../utils";
import { Customer, Trader } from "./reports";
import { Commodities, CommodityOptions } from "./commodity";
import { formatDateWithoutTimezone } from "./date";
import {
  findMatchOption,
  OptionMonthOptions, OlderOptionMonthOptions, formulateOptionMonths, optionLetterToMonthNumber, futureMonths, generateMonthOptionsFor, generateCurrentAndFutureMonthOptions, monthNumberToOptionLetter, generateAccountLabel
} from "./common"
import {
  TradingAccount,
  TradingAccountOpenPosition,
  TradingAccountOpenPositionAudit,
  TradingAccountOpenPositionAdjustment,
  TradingContract,
  PutCallType,
  ProviderType,
} from "./otc/types";

export type {
  TradingAccount,
  TradingAccountOpenPosition,
  TradingAccountOpenPositionAudit,
  TradingAccountOpenPositionAdjustment,
  TradingContract,
};
export { PutCallType, ProviderType };

export {
  findMatchOption,
  CommodityOptions,
  Commodities,
  OptionMonthOptions, OlderOptionMonthOptions, formulateOptionMonths, optionLetterToMonthNumber, futureMonths, generateMonthOptionsFor, generateCurrentAndFutureMonthOptions, monthNumberToOptionLetter, generateAccountLabel
};

export const ActionReviewStagedTradingContractValidation = z.object({
  id: z.string().uuid(),
  should_delete: z.boolean().default(false)
})

export const SingularDateValidation = z.object({
  created_date: z.string(),
});

export const RangeDateValidation = z.object({
  created_range: z.object({
    start: z.string().optional(),
    end: z.string().optional(),
  }),
});

export const RecentReportQueryValidation = z.union([SingularDateValidation, RangeDateValidation]);

export const OtcReportDateRangeValidation = z.object({
  after_date: z.string().min(1),
  before_date: z.string().min(1),
});

export const BushelSummaryReportFiltersValidation = z.object({
  as_of: z.date().optional(),
  effective_as_of: z.date().optional(),
}).refine(
  (filters: { as_of?: Date; effective_as_of?: Date }): boolean =>
    !(filters.as_of && filters.effective_as_of),
  {
    message: "Only one of as_of or effective_as_of may be provided",
    path: ["effective_as_of"],
  },
);

export type BushelSummaryReportFilters = z.infer<typeof BushelSummaryReportFiltersValidation>;
export type OtcReportGenerateBody = typeof OtcReportDateRangeValidation._type;

export interface OtcGeneratedReport {
  tracked_report_id: string;
  document_id: string;
  report_type: "otc_invoice_fees" | "otc_fee_report";
  link: string;
}

export const posNumberKeys = ["bushel", "long_qty", "short_qty", "strike_price", "price", "premium"];
export const contractNumberKeys = [
  "quantity",
  "step_one",
  "step_two",
  "step_three",
  "double_up",
  "option_premium",
  "contract_fee",
  "futures_price",
  "otc_futures",
  "weeks",
  "period_qty",
  "knockout",
  "implied_fx_amount",
  "expected_contract_futures",
  "actual_contract_futures",
  "futures_margin",
  "actual_contract_futures_step_one",
  "actual_contract_futures_step_two",
  "actual_contract_futures_double_up",
];
const otcOpenContractBaseKey = "otcOpenContracts";
const otcOpenPositionsBaseKey = "otcOpenPositions";

export function useListAccounts(listOtcAccountsOnly: boolean) {
  const queryParams = new URLSearchParams();
  if (listOtcAccountsOnly) {
    queryParams.append("listOtcAccountsOnly", "true");
  }

  return useQuery({
    queryKey: ["otcTradingAccounts", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<TradingAccount[]>("otc/open_position_filters", queryParams),
    retry: 1,
  });
}

function getOpenPositionsQueryParams(
  params: PositionListOptionParams,
): URLSearchParams {
  const queryParams = new URLSearchParams();
  if (params.listOtcAccountsOnly) {
    queryParams.append("listOtcAccountsOnly", "true");
  }

  if ((params.filters?.account_ids as string[])?.length > 0) {
    queryParams.append("account_ids", params.filters!.account_ids!.join(","));
  }
  [
    { key: "strike_price", value: params.filters?.strike_price },
    { key: "commodity", value: params.filters?.commodity },
    { key: "option_month", value: params.filters?.option_month },
  ].forEach((item: any) => {
    if (item.value) {
      queryParams.append(item.key, item.value);
    }
  });

  return queryParams;
}

export function useListClosedContracts(params: ContractListOptionParams) {
  const queryParams = getOpenContractQueryParams(params);

  return useQuery({
    queryKey: ["otc", "closed", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<any>("otc/open_contracts/closed", queryParams),
    retry: 1,
  });
}

export function useListBaseOpenContracts(params: ContractListOptionParams) {
  const queryParams = getOpenContractQueryParams(params);

  return useQuery({
    queryKey: ["otc", "staged","open", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<any>("otc/open_contracts", queryParams),
    retry: 1,
  });
}

export function useActionReviewStagedTradingContract() {
  const queryClient = useQueryClient();

  return async (params: z.infer<typeof ActionReviewStagedTradingContractValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<boolean>(
      `/otc/contracts/${params.id}/review`,
      { should_delete: params.should_delete },
      queryParams,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["otc", "staged"] });
    }

    return result;
  };
}

export function useUnArchiveContract() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<boolean>(
      `otc/open_contracts/${id}/unarchive`,
      {},
      queryParams,
    );
    queryClient.invalidateQueries({ queryKey: ["otc", "closed"] });

    return result;
  };
}

// NOTE: these two dont use react-query for performance reasons
export function useListOpenContracts(params: ContractListOptionParams) {
  const [data, setData] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState<any>(false);

  const fetchData = async () => {
    if (isLoading) return;
    try {
      setIsLoading(true);

      const queryParams = getOpenContractQueryParams(params);
      const result = await fetchApiGet<{
        pairedContracts: TradingContract[];
        unpairedContracts: TradingContract[];
      }>("otc/open_contracts_with", queryParams);;

      setData(result);
      setIsLoading(false);

    } catch (error) {
      setIsLoading(false);
      setData({ success: false, error });
    }
  };

  // useEffect(() => {
  //   const intervalId = setInterval(() => { fetchData() }, 10 * 1000);
  //   return () => clearInterval(intervalId)
  // }, [hashObject(params || {})]);

  useEffect(() => {
    fetchData();
  }, [hashObject(params || {})]);


  return { data, fetchData, isLoading } as any;
}

export function useListOpenPositions(params: PositionListOptionParams) {
  const [data, setData] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState<any>(false);

  const fetchData = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);

      const queryParams = getOpenPositionsQueryParams(params);
      const result = await fetchApiGet<{
        pairedPositions: TradingAccountOpenPosition[];
        unpairedPositions: TradingAccountOpenPosition[];
        pairs: PositionContractPair[];
      }>("otc/open_positions", queryParams)
      setData(result);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setData({ success: false, error });
    }
  };

  // useEffect(() => {
  //   const intervalId = setInterval(() => { fetchData() }, 10 * 1000);
  //   return () => clearInterval(intervalId)
  // }, [hashObject(params || {})]);

  useEffect(() => {
    fetchData();
  }, [hashObject(params || {})]);


  return { data, fetchData, isLoading } as any;
}

export function useDownloadMarginReportData() {
  const queryClient = useQueryClient();

  return async (params: MarginReportDownloadParams) => {
    const queryParams = getDownloadMarginReportQueryParams(params);

    const result = await fetchApiPost<string>(
      `otc/margin/download`,
      {},
      queryParams,
    );
    return result;
  };
}

export function useGenerateInvoiceFeesReport() {
  return async (body: OtcReportGenerateBody) => {
    const result = await fetchApiPost<OtcGeneratedReport>(
      `otc/reports/invoice-fees/generate`,
      body,
    );
    return result;
  };
}

export function useGenerateFeeReport() {
  return async (body: OtcReportGenerateBody) => {
    const result = await fetchApiPost<OtcGeneratedReport>(
      `otc/reports/fee-report/generate`,
      body,
    );
    return result;
  };
}

export function useDownloadSummaryData() {
  return async (params: BushelSummaryReportFilters) => {
    const parsed = BushelSummaryReportFiltersValidation.parse(params);
    const queryParams = new URLSearchParams();

    if (parsed.as_of) {
      queryParams.append("as_of", formatDateWithoutTimezone(parsed.as_of));
    }

    if (parsed.effective_as_of) {
      queryParams.append("effective_as_of", formatDateWithoutTimezone(parsed.effective_as_of));
    }

    const result = await fetchApiPost<string>(
      `otc/summary/download`,
      {},
      queryParams,
    );
    return result;
  };
}

export function useDownloadOpenData() {
  const queryClient = useQueryClient();

  return async (params: typeof DownloadFilterValidation._type) => {
    const queryParams = getDownloadQueryParams(params);

    const result = await fetchApiPost<string>(
      `otc/download`,
      {},
      queryParams,
    );
    return result;
  };
}

export function useDownloadOpenRecentContractData() {
  const queryClient = useQueryClient();

  return async (params: { created_date?: string; created_range?: { start?: string; end?: string } }) => {
    const queryParams = new URLSearchParams();

    if (params.created_date) {
      queryParams.append("created_date", params.created_date);
    }
    if (params.created_range) {
      queryParams.append("created_range", JSON.stringify({
        start: params.created_range.start,
        end: params.created_range.end
      }));
    }

    const result = await fetchApiPost<{
      link: string;
      import_new_contracts_link: string;
      import_update_contracts_link: string;
    }>(
      `otc/open_contracts/download/recent`,
      {},
      queryParams,
    );
    return result;
  };
}

export function useDownloadOpenPositions() {
  const queryClient = useQueryClient();

  return async (params: PositionListOptionParams) => {
    const queryParams = getOpenPositionsQueryParams(params);

    const result = await fetchApiPost<string[]>(
      `otc/open_positions/download`,
      {},
      queryParams,
    );
    queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });
    return result;
  };
}

export function useMarkPositionWillExpire() {
  return async (data: MarkPositionWillExpireData) => {
    return fetchApiPatch<TradingAccountOpenPosition>(
      `otc/open_positions/${data.position_id}/will-expire`,
      { will_expire: data.will_expire },
    );
  };
}

export function useMarkPositionManualFullyPaired() {
  const queryClient = useQueryClient();

  return async (data: MarkPositionManualFullyPairedData) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<string[]>(
      `otc/open_positions/${data.position_id}/markFullyPaired`,
      data,
      queryParams,
    );

    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });

    return result;
  };
}

export function useBulkMarkPositionsManualFullyPaired() {
  const queryClient = useQueryClient();

  return async (data: BulkMarkPositionsManualFullyPairedData) => {
    const result = await fetchApiPost<boolean>(
      `otc/open_positions/bulk/markFullyPaired`,
      data,
    );

    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });

    return result;
  };
}

export function useMarkContractManualFullyPaired() {
  const queryClient = useQueryClient();

  return async (data: MarkContractManualFullyPairedData) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<string[]>(
      `otc/open_contracts/${data.contract_id}/markFullyPaired`,
      data,
      queryParams,
    );

    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });

    return result;
  };
}

export function useUnPairContract() {
  const queryClient = useQueryClient();

  return async (data: PairingData) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<string[]>(
      `otc/open_positions/${data.position_id}/unpair`,
      data,
      queryParams,
    );

    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });

    return result;
  };
}

export function usePairContract() {
  const queryClient = useQueryClient();

  return async (data: PairingData) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<string[]>(
      `otc/open_positions/${data.position_id}/pair`,
      data,
      queryParams,
    );

    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });

    return result;
  };
}

function getDownloadMarginReportQueryParams(params: MarginReportDownloadParams) {
  const queryParams = new URLSearchParams();

  [
    { key: "after_date", value: params?.after_date },
    { key: "before_date", value: params?.before_date },

  ].forEach((item: any) => {
    if (item.key.includes("date") && item.value) {
      queryParams.append(item.key, moment.tz(item.value, CommonDestinationTimezone).format("YYYY/MM/DD"));
    } else if (item.value) {
      queryParams.append(item.key, item.value);
    }
  });

  return queryParams;
}

function getDownloadQueryParams(params: typeof DownloadFilterValidation._type) {
  const queryParams = new URLSearchParams();

  [
    { key: "trading_account_ids", value: params?.trading_account_ids },
  ].forEach((item: any) => {
    if (item.value?.length > 0) {
      queryParams.append(item.key, item.value!.join(","));
    }
  });

  [
    { key: "strike_price", value: params?.strike_price },
    { key: "contract_end_date", value: params?.contract_end_date },
    { key: "reference_month", value: params?.reference_month },
    { key: "commodity", value: params?.commodity },
  ].forEach((item: any) => {
    if (item.key === "contract_end_date" && item.value) {
      queryParams.append(item.key, moment.tz(item.value, CommonDestinationTimezone).format("YYYY/MM/DD"));
    } else if (item.value) {
      queryParams.append(item.key, item.value);
    }
  });

  return queryParams;
}

function getOpenContractQueryParams(
  params: ContractListOptionParams,
): URLSearchParams {
  const queryParams = new URLSearchParams();
  if (typeof params.staged_for_review === 'boolean') {
    queryParams.set("staged_for_review", params.staged_for_review.toString());
  }

  [
    { key: "customer_ids", value: params.filters?.customer_ids },
    { key: "account_no_a_ids", value: params.filters?.account_no_a_ids },
    { key: "account_no_b_ids", value: params.filters?.account_no_b_ids },
    { key: "trader_ids", value: params.filters?.trader_ids },
  ].forEach((item: any) => {
    if (item.value?.length > 0) {
      queryParams.append(item.key, item.value!.join(","));
    }
  });

  [
    { key: "contract_no", value: params.filters?.contract_no },
    { key: "date", value: params.filters?.date },
    { key: "end_date", value: params.filters?.end_date },
    { key: "contract_type", value: params.filters?.contract_type },
    { key: "commodity", value: params.filters?.commodity },
    { key: "trade_reference", value: params.filters?.trade_reference },
    { key: "option_month", value: params.filters?.option_month },
    { key: "step_one", value: params.filters?.step_one },
    { key: "step_two", value: params.filters?.step_two },
    { key: "step_three", value: params.filters?.step_three },
    { key: "double_up", value: params.filters?.double_up },
  ].forEach((item: any) => {
    if (item.key.includes("date") && item.value) {
      queryParams.append(item.key, moment.tz(formatDateWithoutTimezone(item.value), "YYYY/MM/DD", CommonDestinationTimezone).format("YYYY/MM/DD"));
    } else if (item.value) {
      queryParams.append(item.key, item.value);
    }
  });

  return queryParams;
}

export function useDownloadOpenContracts() {
  const queryClient = useQueryClient();

  return async (params: ContractListOptionParams) => {
    const queryParams = getOpenContractQueryParams(params);

    const result = await fetchApiPost<string>(
      `otc/open_contracts/download`,
      {},
      queryParams,
    );
    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    return result;
  };
}


export function useListRecentArchivedPositions() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["otcArchivedPositions"],
    queryFn: () => {
      return fetchApiGet<TradingAccountOpenPositionAudit[]>(
        "otc/open_positions/archived",
        queryParams,
      );
    },
    retry: 1,
  });
}

export function useListOpenContractFilters() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["otcOpenContractsFilters"],
    queryFn: () => {
      return fetchApiGet<{ customers: Customer[] }>(
        "otc/open_contracts_filters",
        queryParams,
      );
    },
    retry: 1,
  });
}

export function useEditManualPosition() {
  const queryClient = useQueryClient();

  return async (params: typeof CreatePositionValidation._type, id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<TradingAccountOpenPosition>(
      `otc/open_positions/${id}`,
      params,
      queryParams,
    );
    return result;
  };
}

export function useCreatePosition() {
  const queryClient = useQueryClient();

  return async (params: typeof CreatePositionValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<TradingContract>(
      `otc/open_positions`,
      params,
      queryParams,
    );
    return result;
  };
}

export function useCreateContract() {
  const queryClient = useQueryClient();

  return async (params: typeof CreateContractValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ contract: TradingContract }>(
      `otc/contracts`,
      params,
      queryParams,
    );
    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    return result;
  };
}

export function useUpdateContract() {
  const queryClient = useQueryClient();

  return async (params: typeof UpdateContractValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<{ contract: TradingContract }>(
      `otc/contracts/${params.id}`,
      params,
      queryParams,
    );
    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });

    return result;
  };
}

// ─── OTC Layered Contract API ──────────────────────────────────────────────

export const otcContractLayersBaseKey = "otcContractLayers";

export const OtcLayerStatusValidation = z.enum(["current", "locked", "voided"]);
export const OtcLayerSourceTypeValidation = z.enum([
  "initial",
  "amendment",
  "split_result",
  "migration_backfill",
]);
export const OtcLayerAmendmentTypeValidation = z.enum([
  "split",
  "contract_type_change",
  "contract_legs_change",
  "roll",
  "futures_roll",
  "basis_roll",
  "otc_roll",
  "exit",
  "expired",
]);
export const OtcRollDirectionValidation = z.enum(["up", "down"]);

export const OtcContractLayerDtoValidation = z.object({
  id: z.string().uuid(),
  trading_contract_id: z.string().uuid(),
  layer_no: z.number(),
  status: OtcLayerStatusValidation,
  source_type: OtcLayerSourceTypeValidation,
  amendment_type: OtcLayerAmendmentTypeValidation.nullable().optional(),
  roll_direction: OtcRollDirectionValidation.nullable().optional(),
  adjustment_slot: z.number().nullable().optional(),
  amendment_trader_id: z.string().uuid().nullable().optional(),
  has_contract_adjustment: z.boolean(),
  has_invoice_fees: z.boolean(),
  notes: z.string().nullable().optional(),
  snapshot: z.record(z.unknown()),
  display_snapshot: z.record(z.unknown()),
  field_patch: z.record(z.unknown()),
  owned_fields: z.array(z.string()),
  staged_for_review: z.boolean(),
  reviewed_at: z.string().nullable().optional(),
  reviewed_by_user_id: z.string().uuid().nullable().optional(),
  locked_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  invoice_fee_info: z.object({
    has_been_paid: z.boolean(),
    invoice_number: z.string(),
    date_marked: z.string().nullable(),
    invoice_date: z.string().nullable(),
  }),
});

export const CanCreateAmendmentReasonValidation = z.enum([
  "archived",
  "no_current_layer",
  "max_adjustment_slot_reached",
  "max_layer_no_reached",
  "amendment_layer_pending_review",
]);

export const OtcContractLayersResponseValidation = z.object({
  contract: z.record(z.unknown()),
  current_layer_id: z.string().uuid().nullable(),
  next_adjustment_slot: z
    .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)])
    .nullable(),
  can_create_amendment: z.boolean(),
  can_create_amendment_reason: CanCreateAmendmentReasonValidation.nullable().optional(),
  layers: z.array(OtcContractLayerDtoValidation),
});

export const UpdateCurrentLayerRequestValidation = z.object({
  layer_id: z.string().uuid(),
  data: z.record(z.unknown()),
});

export const CreateAmendmentRequestValidation = z.object({
  expected_current_layer_id: z.string().uuid(),
  amendment_type: OtcLayerAmendmentTypeValidation,
  roll_direction: OtcRollDirectionValidation.optional(),
  payload: z.record(z.unknown()),
});

export type OtcLayerStatus = z.infer<typeof OtcLayerStatusValidation>;
export type OtcLayerSourceType = z.infer<typeof OtcLayerSourceTypeValidation>;
export type OtcLayerAmendmentType = z.infer<typeof OtcLayerAmendmentTypeValidation>;
export type OtcRollDirection = z.infer<typeof OtcRollDirectionValidation>;
export type OtcContractLayerDto = z.infer<typeof OtcContractLayerDtoValidation>;
export type OtcLayerInvoiceFeeInfo = OtcContractLayerDto["invoice_fee_info"];
export type OtcContractLayersResponse = z.infer<typeof OtcContractLayersResponseValidation>;

// ─── OTC Layer Invoice Fee (create / update / delete) ──────────────────────

/**
 * Field-shape validation for creating/updating a layer invoice fee. Dates are
 * `YYYY-MM-DD` portal-date strings (or null); the backend assembles them at
 * midnight in CommonDestinationTimezone. `invoice_number` is always required
 * and non-blank.
 */
export const OtcLayerInvoiceFeeInputValidation = z.object({
  has_been_paid: z.boolean(),
  invoice_number: z.string().min(1),
  invoice_date: z.string().nullable(),
  date_marked: z.string().nullable(),
});

/**
 * Cross-field refinement: `date_marked` is required when `has_been_paid` is
 * true. Used by the form at submit time; the hooks accept the base inferred
 * type so the bulk item schema can `.extend`/reuse it.
 */
export const OtcLayerInvoiceFeeInputRefined = OtcLayerInvoiceFeeInputValidation.refine(
  (data: z.infer<typeof OtcLayerInvoiceFeeInputValidation>) =>
    !data.has_been_paid ||
    (data.date_marked !== null && data.date_marked.trim() !== ""),
  {
    message: "Date marked is required when marked as paid",
    path: ["date_marked"],
  },
);

/** Bulk create item: the single input plus the target layer id. */
export const BulkCreateOtcLayerInvoiceFeeItemValidation =
  OtcLayerInvoiceFeeInputValidation.extend({
    trading_contract_layer_id: z.string().uuid(),
  });

export type OtcLayerInvoiceFeeInput = z.infer<
  typeof OtcLayerInvoiceFeeInputValidation
>;
export type BulkCreateOtcLayerInvoiceFeeItem = z.infer<
  typeof BulkCreateOtcLayerInvoiceFeeItemValidation
>;

/** Response DTO returned by create/update. */
export interface OtcLayerInvoiceFeeDto {
  id: string;
  trading_contract_layer_id: string;
  has_been_paid: boolean;
  invoice_number: string;
  invoice_date: string | null;
  date_marked: string | null;
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}
export type CanCreateAmendmentReason = z.infer<typeof CanCreateAmendmentReasonValidation>;

export interface UpdateCurrentLayerResponse {
  contract: Record<string, unknown>;
  layer_id: string;
  audit_id: string | null;
  amendment_language: string;
}

export interface CreateAmendmentResponse {
  event_id: string;
  previous_layer_id: string;
  new_layer_id: string;
  audit_id: string;
  amendment_language: string;
}

export function useOtcContractLayers(contract_id: string | null) {
  return useQuery({
    queryKey: [otcContractLayersBaseKey, contract_id],
    queryFn: () =>
      fetchApiGet<OtcContractLayersResponse>(
        `otc/contracts/${contract_id}/layers`,
      ),
    enabled: !!contract_id,
    retry: 1,
  });
}

export function useUpdateOtcContractCurrentLayer() {
  const queryClient = useQueryClient();

  return async (
    contract_id: string,
    params: z.infer<typeof UpdateCurrentLayerRequestValidation>,
  ) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<UpdateCurrentLayerResponse>(
      `otc/contracts/${contract_id}/layers`,
      params,
      queryParams,
    );
    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [otcContractLayersBaseKey, contract_id],
      });
      queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
      queryClient.invalidateQueries({ queryKey: ["otc"] });
    }

    return result;
  };
}

export function useCreateOtcContractAmendment() {
  const queryClient = useQueryClient();

  return async (
    contract_id: string,
    params: z.infer<typeof CreateAmendmentRequestValidation>,
  ) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<CreateAmendmentResponse>(
      `otc/contracts/${contract_id}/amendments`,
      params,
      queryParams,
    );
    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [otcContractLayersBaseKey, contract_id],
      });
      queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
      queryClient.invalidateQueries({ queryKey: ["otc"] });
    }

    return result;
  };
}

export function useCreateOtcLayerInvoiceFee(contract_id: string) {
  const queryClient = useQueryClient();

  return async (layer_id: string, input: OtcLayerInvoiceFeeInput) => {
    const result = await fetchApiPost<OtcLayerInvoiceFeeDto>(
      `otc/layers/${layer_id}/invoice-fee`,
      input,
    );
    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [otcContractLayersBaseKey, contract_id],
      });
      queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
      queryClient.invalidateQueries({ queryKey: ["otc"] });
    }
    return result;
  };
}

export function useBulkCreateOtcLayerInvoiceFees(contract_id: string) {
  const queryClient = useQueryClient();

  return async (items: BulkCreateOtcLayerInvoiceFeeItem[]) => {
    const result = await fetchApiPost<OtcLayerInvoiceFeeDto[]>(
      `otc/invoice-fee/bulk`,
      items,
    );
    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [otcContractLayersBaseKey, contract_id],
      });
      queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
      queryClient.invalidateQueries({ queryKey: ["otc"] });
    }
    return result;
  };
}

export function useUpdateOtcLayerInvoiceFee(contract_id: string) {
  const queryClient = useQueryClient();

  return async (layer_id: string, input: OtcLayerInvoiceFeeInput) => {
    const result = await fetchApiPut<OtcLayerInvoiceFeeDto>(
      `otc/layers/${layer_id}/invoice-fee`,
      input,
    );
    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [otcContractLayersBaseKey, contract_id],
      });
      queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
      queryClient.invalidateQueries({ queryKey: ["otc"] });
    }
    return result;
  };
}

export function useDeleteOtcLayerInvoiceFee(contract_id: string) {
  const queryClient = useQueryClient();

  return async (layer_id: string) => {
    const result = await fetchApiDelete<undefined>(
      `otc/layers/${layer_id}/invoice-fee`,
    );
    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [otcContractLayersBaseKey, contract_id],
      });
      queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
      queryClient.invalidateQueries({ queryKey: ["otc"] });
    }
    return result;
  };
}

export function useArchiveContract() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<boolean>(
      `otc/open_contracts/${id}/archive`,
      {},
      queryParams,
    );
    queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });

    return result;
  };
}

export function useArchivePosition() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<boolean>(
      `/otc/open_positions/${id}/archive`,
      {},
      queryParams,
    );
    queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });
    queryClient.invalidateQueries({ queryKey: ["otcArchivedPositions"] });

    return result;
  };
}

export interface MarginReportDownloadParams {
  after_date?: Date;
  before_date?: Date;
}

export interface PositionListOptionParams {
  filters: {
    account_ids?: string[];
    strike_price?: string;
    commodity?: string;
    option_month?: string;
  };
  listOtcAccountsOnly?: boolean;
}




export enum ContractType {
  BasePriceReown = "base_price_reown",
  Range = "range",
  RangePriceReown = "range_price_reown",
  RangeWDU = "range_w_du",
  MinMax = "min_max",
  PremiumOfferWDU = "premium_offer_w_du",
  DoubleUp = "double_up",
  MinPriceCallSpread = "min_price_call_spread",
  MinimumPrice = "minimum_price",
  MinMaxWDU = "min_max_w_du",
  AccWeeklyDU = "acc_weekly_du",
  ReOwnWDU = "reown_w_du",
  HedgePlus = "hedge_plus",
}

export const PutOrCallOptions = [
  { value: PutCallType.Put, label: "Put" },
  { value: PutCallType.Call, label: "Call" },
];

export const FlatPutOrCallOptions = [
  { value: "P", label: "Put" },
  { value: "C", label: "Call" },
];

export const ContractTypeOptions = [
  { value: ContractType.BasePriceReown, label: "Price Re-ownership" },
  { value: ContractType.Range, label: "Range" },
  { value: ContractType.RangePriceReown, label: "Range Price Re-ownership" },
  { value: ContractType.RangeWDU, label: "Range w/DU" },
  { value: ContractType.MinMax, label: "Min/Max" },
  { value: ContractType.HedgePlus, label: "Hedge Plus" },
  { value: ContractType.PremiumOfferWDU, label: "Premium Offer W/DU" },
  { value: ContractType.DoubleUp, label: "Double Up" },
  { value: ContractType.MinPriceCallSpread, label: "Min Price Call Spread" },
  { value: ContractType.MinimumPrice, label: "Min Price" },
  { value: ContractType.AccWeeklyDU, label: "Accumulator Wkly/DU" },
  { value: ContractType.MinMaxWDU, label: "Min/Max W/DU" },
  { value: ContractType.ReOwnWDU, label: "REOWN W/DU" },
];

export const DownloadRecentContractFilterValidation = z.object({
  created_date: z.string().optional().nullable(),
  created_range: z.object({
    start: z.string().optional(),
    end: z.string().optional(),
  }).optional().nullable(),
});


export const DownloadFilterValidation = z.object({
  trading_account_ids: z.array(z.string().uuid()).optional(),
  reference_month: z.string().optional(),
  strike_price: z.string().optional(),
  contract_end_date: z.string().min(1),
  commodity: z.string().min(1).optional(),
});

export const CreatePositionValidation = z.object({
  trading_account_id: z.string().uuid(),

  date: z.string().min(1),
  option_month: z.string().min(1),

  long_qty: z.string(),
  short_qty: z.string(),

  put_or_call: z.string().optional(),
  contract: z.string(),

  commodity: z.string().min(1).optional(),
  bushel: z.string().min(1).optional(),
  strike_price: z.string().min(1).optional(),

  price: z.string().min(1),
  premium: z.string().min(1),
});

export const UpdatePositionValidation = CreatePositionValidation.extend({
  id: z.string().uuid(),
});

export const BaseContractValidation = z.object({
  id: z.string().uuid().optional(),

  contract_type: z.string().min(1),
  contract_no: z.string().min(1).optional(),

  date: z.string(),
  end_date: z.string(),

  shipment_start_date: z.string(),
  shipment_end_date: z.string(),

  notes: z.string().optional(),

  trader_id: z.string().uuid(),
  account_no_a: z.string().uuid(),

  trade_reference: z.string().min(1).optional(),
  contract_reference: z.string().min(1).optional(),
  customer_id: z.string().uuid(),

  quantity: z.string().min(1),
  commodity: z.string().min(1),

  option_month: z.string().min(1),

  option_premium: z.string().min(1),
  contract_fee: z.string().min(1),
  margin: z.string().optional(),
  margin_amount: z.string().optional(),

  adj_option_premium: z.string().optional(),
  adj_margin: z.string().optional(),
  adj_margin_amount: z.string().optional(),
  adj_contract_fee: z.string().optional(),
  last_adjusted_date: z.string().optional(),

  implied_fx_amount: z.string().optional(),

  expected_contract_futures: z.string().optional(),
  actual_contract_futures: z.string().optional(),
  futures_margin: z.string().optional(),

  expected_contract_futures_step_one: z.string().optional(),
  expected_contract_futures_step_two: z.string().optional(),
  expected_contract_futures_double_up: z.string().optional(),
  actual_contract_futures_step_one: z.string().optional(),
  actual_contract_futures_step_two: z.string().optional(),
  actual_contract_futures_double_up: z.string().optional(),
  futures_margin_step_one: z.string().optional(),
  futures_margin_step_two: z.string().optional(),
  futures_margin_double_up: z.string().optional(),

  final_futures_expected_price: z.string().optional(),
  final_futures_price: z.string().optional(),
  final_futures_price_date: z.string().optional(),
  final_futures_price_margin: z.string().optional(),

  crop_year: z.number().int().min(2016),

  rolled_option_month: z.string().min(1).optional(),
  rolled_basis_month: z.string().min(1).optional(),

  carry: z.string().optional(),
  basis_carry: z.string().optional(),

  has_invoice_fees: z.boolean().optional(),
  has_contract_adjustment: z.boolean().optional(),
});

export const BasePriceReownValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.BasePriceReown),
  contract_reference: z.string().min(1).optional(),
  step_one: z.string().min(1),
  step_two: z.string().min(1),
});

export const RangeValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.Range),
  step_one: z.string().min(1),
  step_two: z.string().min(1),
  step_three: z.string().min(1),
});

export const RangePriceReownValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.RangePriceReown),
  contract_reference: z.string().min(1).optional(),
  step_one: z.string().min(1),
  step_two: z.string().min(1),
  step_three: z.string().min(1),
});

export const RangeWDUValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.RangeWDU),
  option_month_etc_one: z.string().min(1),
  step_one: z.string().min(1),
  step_two: z.string().min(1),
  step_three: z.string().min(1),
  double_up: z.string().min(1),
});

export const ReOwnWDUValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.ReOwnWDU),
  contract_reference: z.string().min(1).optional(),
  option_month_etc_one: z.string().min(1),
  step_one: z.string().min(1),
  step_two: z.string().min(1),
  step_three: z.string().min(1),
});

export const MinMaxValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.MinMax),
  step_one: z.string().min(1),
  step_three: z.string().min(1),
});

export const HedgePlusValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.HedgePlus),
  step_one: z.string().min(1),
});

export const PremiumValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.PremiumOfferWDU),
  option_month_etc_one: z.string().min(1),
  account_no_b: z.string().uuid(),
  futures_price: z.string(),
  otc_futures: z.string(),
  step_three: z.string().min(1),
});

export const DoubleUpValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.DoubleUp),
  option_month_etc_one: z.string().min(1),
  step_three: z.string().min(1),
});

export const MinPriceValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.MinPriceCallSpread),
  step_one: z.string().min(1),
  step_three: z.string().min(1),
});

export const MinimumPriceValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.MinimumPrice),
  step_three: z.string().min(1),
});

export const AccumulatorValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.AccWeeklyDU),
  option_month_etc_one: z.string().min(1),
  start_date: z.string(),

  weeks: z.string(),
  period_qty: z.string(),
  knockout: z.string(),

  step_one: z.string().min(1),
});

export const MinMaxDoubleUpValidation = BaseContractValidation.extend({
  contract_type: z.literal(ContractType.MinMaxWDU),
  option_month_etc_one: z.string().min(1),

  step_one: z.string().min(1),
  step_two: z.string().min(1),
  step_three: z.string().min(1),
});

export const CreateContractValidation = MinMaxDoubleUpValidation.or(RangeValidation.or(
  MinMaxValidation.or(
    PremiumValidation.or(DoubleUpValidation).or(MinPriceValidation).or(AccumulatorValidation).or(MinimumPriceValidation).or(ReOwnWDUValidation).or(HedgePlusValidation).or(RangeWDUValidation).or(RangePriceReownValidation).or(BasePriceReownValidation),
  ),
));

export const UpdateContractValidation = MinMaxDoubleUpValidation.partial().or(RangeValidation.partial().or(
  MinMaxValidation.partial().or(
    PremiumValidation.partial().or(
      MinPriceValidation.partial().or(AccumulatorValidation.partial().or(MinimumPriceValidation.partial().or(ReOwnWDUValidation.partial().or(DoubleUpValidation.partial().or(HedgePlusValidation.partial().or(RangeWDUValidation.partial().or(RangePriceReownValidation.partial().or(BasePriceReownValidation.partial())))))))),
    ),
  ),
));

export interface ContractListOptionParams {
  staged_for_review?: boolean;
  filters: {
    contract_no?: string;
    customer_ids?: string[];
    trader_ids?: string[];
    account_no_a_ids?: string[];
    account_no_b_ids?: string[];
    date?: Date;
    end_date?: Date;
    contract_type?: string;
    commodity?: string;
    trade_reference?: string;
    option_month?: string;
    step_one?: string;
    step_two?: string;
    step_three?: string;
    double_up?: string;
  };
}

export function createContractsExcelBuffer({
  contracts,
}: {
  contracts: TradingContract[];
}) {
  const workbook = new ExcelJS.Workbook();

  const sheet = workbook.addWorksheet(
    moment.tz(CommonDestinationTimezone).format("YYYY"),
  );

  sheet.addRow(generateOpenContractColumns());

  for (const row of contracts.map(transformToOpenContractsToCsv)) {
    const formattedRow = [
      row["Date"],
      row["Open/Closed"],
      row["Brokerage HTA"],
      row["Brokerage OTC"],
      row["Contract #"],
      row["Customer"],
      row["Type"],
      row["Quantity"],
      row["Commodity"],
      row["Ref Month"],
      row["Start Date"],
      row["End Date"],
      row["Weeks"],
      row["Period Qty"],
      row["Knockout"],
      row["Step 1"],
      row["Step 2"],
      row["Step 3"],
      row["Opt Prem"],
      row["Margin"],
      row["Contract Fee"],
      row["Trade Reference"],
      row["Futures Price"],
      row["OTC Futures"],
      row["Notes"],
    ];
    sheet.addRow(formattedRow);
  }

  sheet.getColumn("A").numFmt = Excel.CellFormat.MonthDate;
  sheet.getColumn("H").numFmt = Excel.CellFormat.NON_CURRENCY_ACCOUNTING;
  sheet.getColumn("J").numFmt = Excel.CellFormat.MonthDate;
  sheet.getColumn("K").numFmt = Excel.CellFormat.MonthDate;

  sheet.getColumn("M").numFmt = Excel.CellFormat.NON_CURRENCY_ACCOUNTING;
  sheet.getColumn("N").numFmt = Excel.CellFormat.NON_CURRENCY_ACCOUNTING;
  sheet.getColumn("O").numFmt = Excel.CellFormat.ACCOUNTING;
  sheet.getColumn("P").numFmt = Excel.CellFormat.ACCOUNTING;
  sheet.getColumn("Q").numFmt = Excel.CellFormat.ACCOUNTING;
  sheet.getColumn("R").numFmt = Excel.CellFormat.ACCOUNTING;
  sheet.getColumn("S").numFmt = Excel.CellFormat.ACCOUNTING;
  sheet.getColumn("T").numFmt = Excel.CellFormat.ACCOUNTING;
  sheet.getColumn("U").numFmt = Excel.CellFormat.ACCOUNTING;

  sheet.getColumn("W").numFmt = Excel.CellFormat.ACCOUNTING;
  sheet.getColumn("X").numFmt = Excel.CellFormat.NON_CURRENCY_ACCOUNTING;

  sheet.getColumn("A").width = 11;
  sheet.getColumn("B").width = 10;
  sheet.getColumn("C").width = 10;
  sheet.getColumn("D").width = 10;
  sheet.getColumn("E").width = 15;
  sheet.getColumn("F").width = 30;
  sheet.getColumn("G").width = 30;
  sheet.getColumn("H").width = 15;
  sheet.getColumn("K").width = 11;
  sheet.getColumn("L").width = 11;
  sheet.getColumn("P").width = 11;
  sheet.getColumn("Q").width = 11;
  sheet.getColumn("R").width = 11;
  sheet.getColumn("S").width = 11;
  sheet.getColumn("T").width = 11;
  sheet.getColumn("U").width = 11;
  sheet.getColumn("V").width = 11;
  sheet.getColumn("W").width = 11;
  sheet.getColumn("X").width = 11;

  return Excel.workbookToBuffer(workbook);
}

function generateOpenContractColumns() {
  return [
    "Date",
    "Open/Closed",
    "Brokerage HTA",
    "Brokerage OTC",
    "Contract #",
    "Customer",
    "Type",
    "Quantity",
    "Commodity",
    "Option Month",
    "Start Date",
    "End Date",
    "Weeks",
    "Period Qty",
    "Knockout",
    "Step 1",
    "Step 2",
    "Step 3",
    "Opt Prem",
    "Margin",
    "Contract Fee",
    "Trade Reference",
    "Futures Price",
    "OTC Futures",
    "Notes",
  ];
}

function transformToOpenContractsToCsv(row: TradingContract) {
  return {
    Date: moment.tz(row.date, CommonDestinationTimezone).format("MM/DD/YYYY"),
    "Open/Closed": "Open",
    "Brokerage HTA": row.AccountNoB?.account_id || "",
    "Brokerage OTC": row.AccountNoA?.account_id || "",
    "Contract #": row.contract_no || "",
    Customer: row.Customer?.long_name || "",
    Type: findMatchOption(ContractTypeOptions, row.contract_type)?.label || "",
    Quantity: row.quantity ? new Decimal(row.quantity).toNumber() || "" : "",
    Commodity: findMatchOption(CommodityOptions, row.commodity)?.label || "",
    "Ref Month": row.option_month,
    "Start Date": row.start_date
      ? moment
        .tz(row.start_date, CommonDestinationTimezone)
        .format("MM/DD/YYYY")
      : "",
    "End Date": row.end_date
      ? moment.tz(row.end_date, CommonDestinationTimezone).format("MM/DD/YYYY")
      : "",
    Weeks: row.weeks ? new Decimal(row.weeks)?.toNumber() || "" : "",
    "Period Qty": row.period_qty
      ? new Decimal(row.period_qty)?.toNumber() || ""
      : "",
    Knockout: row.knockout ? new Decimal(row.knockout).toNumber() || "" : "",
    "Step 1": row.step_one ? new Decimal(row.step_one)?.toNumber() || "" : "",
    "Step 2": row.step_two ? new Decimal(row.step_two)?.toNumber() || "" : "",
    "Step 3": row.step_three
      ? new Decimal(row.step_three)?.toNumber() || ""
      : "",
    "Double Up": row.double_up
      ? new Decimal(row.double_up)?.toNumber() || ""
      : "",
    "Opt Prem": row.option_premium
      ? new Decimal(row.option_premium).toNumber() || ""
      : "",
    Margin: row.margin ? new Decimal(row.margin).toNumber() || "" : "", // Lighthouse Fee?
    "Contract Fee": row.contract_fee
      ? new Decimal(row.contract_fee).toNumber() || ""
      : "", // Initial Premium + Fee?
    "Trade Reference": row.trade_reference || "",
    "Futures Price": row.futures_price
      ? new Decimal(row.futures_price)?.toNumber() || ""
      : "",
    "OTC Futures": row.otc_futures
      ? new Decimal(row.otc_futures)?.toNumber() || ""
      : "",
    Notes: row.notes || "",
    "Margin Amount": row.margin_amount
      ? new Decimal(row.margin_amount).toNumber() || ""
      : "",
  };
}

interface PairingData {
  position_id: string;
  contract_id: string;
}

interface MarkPositionWillExpireData {
  position_id: string;
  will_expire: boolean;
}

interface MarkPositionManualFullyPairedData {
  is_fully_paired: boolean;
  position_id: string;
}

interface BulkMarkPositionsManualFullyPairedData {
  position_ids: string[];
}

interface MarkContractManualFullyPairedData {
  is_fully_paired: boolean;
  contract_id: string;
}

export interface PositionContractPair extends BaseDbObject {
  trading_contract_id?: string;
  trading_position_id?: string;
  trading_archived_contract_id?: string;
  trading_archived_position_id?: string;

  user_id: string;

  Contract?: TradingContract;
  Position?: TradingAccountOpenPosition;
  ArchivedContract?: ArchivedTradingContract;
  ArchivedPosition?: TradingAccountOpenPositionAudit;

  User?: DbUser;
}

export interface ArchivedTradingContract extends BaseDbObject {
  contract_type: string;
  contract_no?: string;
  contract_reference?: string;

  date: Date;

  is_manually_fully_paired: boolean;

  start_date?: Date;
  end_date?: Date;

  shipment_start_date?: Date;
  shipment_end_date?: Date;

  futures_price?: Decimal;
  otc_futures?: Decimal; // price?

  weeks?: Decimal;
  period_qty?: Decimal;
  knockout?: Decimal;
  notes?: string;

  account_no_a: string;
  account_no_b?: string;

  trade_reference?: string;
  customer_id: string;
  trader_id?: string;

  quantity: Decimal;
  commodity: string;

  option_month: string;
  step_one?: Decimal;
  step_two?: Decimal;
  step_three?: Decimal;
  double_up?: Decimal;

  option_premium: Decimal;
  contract_fee: Decimal;
  margin: Decimal;
  margin_amount: Decimal;

  adj_option_premium?: Decimal;
  adj_margin?: Decimal;
  adj_margin_amount?: Decimal;
  adj_contract_fee?: Decimal;
  last_adjusted_date?: Date;

  final_futures_expected_price?: Decimal;
  final_futures_price?: Decimal;
  final_futures_price_date?: Date;
  final_futures_price_margin?: Decimal;

  rolled_option_month?: string;
  rolled_basis_month?: string;

  carry?: Decimal;
  basis_carry?: Decimal;

  Trader?: Trader;
  AccountNoA: TradingAccount;
  AccountNoB?: TradingAccount;
  Customer?: Customer;
}

export const CreatePositionAdjustmentValidation = z.object({
  position_ids: z.array(z.string().uuid()).min(2),
  valid_from: z.string().min(1),
  valid_to: z.string().min(1),
});

export interface PositionAdjustmentFilters {
  valid_from?: string;
  valid_to?: string;
}

const positionAdjustmentsBaseKey = "otcPositionAdjustments";

export function useCreatePositionAdjustment() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreatePositionAdjustmentValidation>) => {
    const result = await fetchApiPost<TradingAccountOpenPositionAdjustment>(
      "otc/position-adjustments",
      data,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [positionAdjustmentsBaseKey] });
      queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });
      queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    }
    return result;
  };
}

export function useDeletePositionAdjustment() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiDelete<TradingAccountOpenPositionAdjustment>(
      `otc/position-adjustments/${id}`,
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [positionAdjustmentsBaseKey] });
      queryClient.invalidateQueries({ queryKey: [otcOpenPositionsBaseKey] });
      queryClient.invalidateQueries({ queryKey: [otcOpenContractBaseKey] });
    }
    return result;
  };
}

export function useListPositionAdjustments(filters: PositionAdjustmentFilters) {
  const queryParams = new URLSearchParams();
  if (filters.valid_from) queryParams.set("valid_from", filters.valid_from);
  if (filters.valid_to) queryParams.set("valid_to", filters.valid_to);

  return useQuery({
    queryKey: [positionAdjustmentsBaseKey, queryParams.toString()],
    queryFn: () => fetchApiGet<TradingAccountOpenPositionAdjustment[]>(
      "otc/position-adjustments",
      queryParams,
    ),
    retry: 1,
  });
}
